import { 
    Box, 
    Container, 
    Grid, 
    useMediaQuery, 
    Fade, 
    Zoom,
    Typography
} from '@mui/material';
import { School } from '@mui/icons-material';
import React, { useEffect, useState } from 'react'
import Network from './network/Network';
import instId from './network/instituteId';
import { useNavigate } from 'react-router-dom';
import Endpoints from './network/endpoints';

const TestSeriesSection = ({ endpointsUrl }) => {

    const isMobile = useMediaQuery("(max-width:900px)");
    const isTablet = useMediaQuery("(max-width:1200px)");
    const [hoveredCard, setHoveredCard] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const navigate = useNavigate();
    const [coursesList, setCoursesList] = useState([]);

    useEffect(() => {
        getAllCourses();
        // Trigger animation after component mounts
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const getAllCourses = async () => {
        const response = await Network.fetchCourses(instId);
        const course = response?.courses;
        const filterCourses = course?.filter((item) =>
            item?.active === true &&
            ["CA Foundation", "CA Inter", "CA Final"].includes(item?.title)
        );
        setCoursesList(filterCourses);
    };

    const handleCourseClick = (value) => {
        navigate('/course-content', { state: { data: value } })
    }

    const getCourseGradient = (title) => {
        if (title?.includes('Foundation')) return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        if (title?.includes('Inter')) return 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)';
        if (title?.includes('Final')) return 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)';
        return 'linear-gradient(135deg, #e0c3fc 0%, #9bb5ff 100%)';
    }

    const getImageColors = (title) => {
        if (title?.includes('Foundation')) return {
            primary: 'rgba(102, 126, 234, 0.1)',
            secondary: 'rgba(118, 75, 162, 0.08)',
            accent: '#667eea'
        };
        if (title?.includes('Inter')) return {
            primary: 'rgba(255, 236, 210, 0.3)',
            secondary: 'rgba(252, 182, 159, 0.2)',
            accent: '#fcb69f'
        };
        if (title?.includes('Final')) return {
            primary: 'rgba(168, 237, 234, 0.25)',
            secondary: 'rgba(254, 214, 227, 0.15)',
            accent: '#a8edea'
        };
        return {
            primary: 'rgba(224, 195, 252, 0.2)',
            secondary: 'rgba(155, 181, 255, 0.1)',
            accent: '#e0c3fc'
        };
    }

    return (
        <Box
            sx={{
                position: 'relative',
                py: { xs: 4, md: 6 },
                px: { xs: 2, md: 4 },
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 30%, #f1f5f9 70%, #ffffff 100%)',
                overflow: 'hidden',
                minHeight: '70vh',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                        radial-gradient(circle at 15% 20%, rgba(167, 139, 250, 0.05) 0%, transparent 40%),
                        radial-gradient(circle at 85% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 40%),
                        radial-gradient(circle at 50% 50%, rgba(236, 72, 153, 0.03) 0%, transparent 50%)
                    `,
                    pointerEvents: 'none',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                        linear-gradient(45deg, transparent 48%, rgba(255, 255, 255, 0.5) 49%, rgba(255, 255, 255, 0.5) 51%, transparent 52%),
                        linear-gradient(-45deg, transparent 48%, rgba(255, 255, 255, 0.3) 49%, rgba(255, 255, 255, 0.3) 51%, transparent 52%)
                    `,
                    backgroundSize: '60px 60px, 40px 40px',
                    opacity: 0.4,
                    animation: 'subtleMove 20s linear infinite',
                    pointerEvents: 'none',
                    '@keyframes subtleMove': {
                        '0%': { transform: 'translateX(0) translateY(0)' },
                        '100%': { transform: 'translateX(60px) translateY(40px)' },
                    },
                }
            }}
        >
            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Header Section */}
                <Fade in={isVisible} timeout={800}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography 
                            variant="h2"
                            sx={{ 
                                mb: 3,
                                fontWeight: 800,
                                color: 'rgba(30, 41, 59, 0.9)',
                                fontSize: { xs: '2.2rem', md: '3.5rem', lg: '3rem' },
                                position: 'relative',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #a8edea 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundSize: '200% 200%',
                                animation: 'gradientShift 6s ease-in-out infinite',
                                textShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -12,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 120,
                                    height: 5,
                                    background: 'linear-gradient(135deg, #667eea 0%, #a8edea 100%)',
                                    borderRadius: 3,
                                    animation: 'underlineGlow 3s ease-in-out infinite',
                                },
                                '@keyframes gradientShift': {
                                    '0%, 100%': { backgroundPosition: '0% 50%' },
                                    '50%': { backgroundPosition: '100% 50%' },
                                },
                                '@keyframes underlineGlow': {
                                    '0%, 100%': { 
                                        boxShadow: '0 0 10px rgba(102, 126, 234, 0.3)',
                                        transform: 'translateX(-50%) scale(1)'
                                    },
                                    '50%': { 
                                        boxShadow: '0 0 20px rgba(168, 237, 234, 0.5)',
                                        transform: 'translateX(-50%) scale(1.1)'
                                    },
                                },
                            }}
                        >
                            CA Test Series
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                color: 'rgba(51, 65, 85, 0.7)',
                                fontSize: { xs: '1.1rem', md: '1.4rem', lg: '1.6rem' },
                                fontWeight: 500,
                                maxWidth: 700,
                                mx: 'auto',
                                lineHeight: 1.7,
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: -20,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 60,
                                    height: 2,
                                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(168, 237, 234, 0.3) 100%)',
                                    borderRadius: 1,
                                }
                            }}
                        >
                            Master your CA preparation with our comprehensive test series
                        </Typography>
                    </Box>
                </Fade>

                {/* Courses Grid - Image Only */}
                {coursesList?.length > 0 && (
                    <Grid container spacing={{ xs: 3, md: 4, lg: 4 }} justifyContent="center">
                        {coursesList.map((item, index) => {
                            const colors = getImageColors(item?.title);
                            return (
                                <Grid item xs={12} sm={4} md={3} lg={4} key={index}>
                                    <Zoom in={isVisible} timeout={800 + index * 150}>
                                        <Box
                                            onMouseEnter={() => setHoveredCard(index)}
                                            onMouseLeave={() => setHoveredCard(null)}
                                            onClick={() => handleCourseClick(item)}
                                            sx={{
                                                position: 'relative',
                                                aspectRatio: '1',
                                                borderRadius: '32px',
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                background: hoveredCard === index 
                                                    ? `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`
                                                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.7) 100%)',
                                                border: hoveredCard === index 
                                                    ? `3px solid ${colors.accent}`
                                                    : '2px solid rgba(255, 255, 255, 0.8)',
                                                transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                transform: hoveredCard === index 
                                                    ? 'translateY(-16px) scale(1.08) rotateZ(3deg)' 
                                                    : 'translateY(0) scale(1) rotateZ(0deg)',
                                                transformStyle: 'preserve-3d',
                                                boxShadow: hoveredCard === index 
                                                    ? `0 30px 60px ${colors.primary}, 0 15px 35px rgba(0, 0, 0, 0.1)`
                                                    : '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 15px rgba(0, 0, 0, 0.04)',
                                                backdropFilter: 'blur(20px)',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: hoveredCard === index 
                                                        ? `conic-gradient(from 0deg at 50% 50%, ${colors.accent}40, transparent 120deg, ${colors.accent}60, transparent 240deg, ${colors.accent}40)`
                                                        : 'none',
                                                    borderRadius: '32px',
                                                    opacity: hoveredCard === index ? 0.6 : 0,
                                                    transition: 'all 0.6s ease',
                                                    zIndex: -1,
                                                    animation: hoveredCard === index ? 'rotateGlow 4s linear infinite' : 'none',
                                                    '@keyframes rotateGlow': {
                                                        '0%': { transform: 'rotate(0deg) scale(1)' },
                                                        '50%': { transform: 'rotate(180deg) scale(1.02)' },
                                                        '100%': { transform: 'rotate(360deg) scale(1)' },
                                                    }
                                                },
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: hoveredCard === index 
                                                        ? `radial-gradient(circle, ${colors.accent}20 0%, transparent 70%)`
                                                        : 'none',
                                                    borderRadius: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    opacity: hoveredCard === index ? 1 : 0,
                                                    transition: 'all 0.6s ease',
                                                    animation: hoveredCard === index ? 'pulse 3s ease-in-out infinite' : 'none',
                                                    '@keyframes pulse': {
                                                        '0%, 100%': { transform: 'translate(-50%, -50%) scale(0.8)', opacity: 0.3 },
                                                        '50%': { transform: 'translate(-50%, -50%) scale(1.2)', opacity: 0.1 },
                                                    }
                                                }
                                            }}
                                        >
                                            {/* Floating Particles */}
                                            {hoveredCard === index && (
                                                <>
                                                    {[...Array(6)].map((_, i) => (
                                                        <Box
                                                            key={i}
                                                            sx={{
                                                                position: 'absolute',
                                                                width: `${Math.random() * 6 + 3}px`,
                                                                height: `${Math.random() * 6 + 3}px`,
                                                                background: colors.accent,
                                                                borderRadius: '50%',
                                                                top: `${Math.random() * 100}%`,
                                                                left: `${Math.random() * 100}%`,
                                                                opacity: 0.6,
                                                                animation: `float${i} ${3 + Math.random() * 2}s ease-in-out infinite`,
                                                                animationDelay: `${Math.random() * 2}s`,
                                                                '@keyframes float0': {
                                                                    '0%, 100%': { transform: 'translateY(0px) translateX(0px) scale(1)' },
                                                                    '33%': { transform: 'translateY(-15px) translateX(8px) scale(1.2)' },
                                                                    '66%': { transform: 'translateY(5px) translateX(-5px) scale(0.8)' },
                                                                },
                                                                '@keyframes float1': {
                                                                    '0%, 100%': { transform: 'translateY(0px) translateX(0px) scale(1)' },
                                                                    '50%': { transform: 'translateY(-20px) translateX(-10px) scale(1.1)' },
                                                                },
                                                                '@keyframes float2': {
                                                                    '0%, 100%': { transform: 'translateY(0px) translateX(0px) scale(1)' },
                                                                    '25%': { transform: 'translateY(-8px) translateX(12px) scale(1.3)' },
                                                                    '75%': { transform: 'translateY(8px) translateX(-8px) scale(0.9)' },
                                                                },
                                                                '@keyframes float3': {
                                                                    '0%, 100%': { transform: 'translateY(0px) translateX(0px) scale(1)' },
                                                                    '40%': { transform: 'translateY(-12px) translateX(6px) scale(1.1)' },
                                                                    '80%': { transform: 'translateY(4px) translateX(-12px) scale(1.2)' },
                                                                },
                                                                '@keyframes float4': {
                                                                    '0%, 100%': { transform: 'translateY(0px) translateX(0px) scale(1)' },
                                                                    '60%': { transform: 'translateY(-18px) translateX(-6px) scale(0.8)' },
                                                                },
                                                                '@keyframes float5': {
                                                                    '0%, 100%': { transform: 'translateY(0px) translateX(0px) scale(1)' },
                                                                    '30%': { transform: 'translateY(-10px) translateX(15px) scale(1.4)' },
                                                                    '70%': { transform: 'translateY(12px) translateX(-10px) scale(0.7)' },
                                                                },
                                                            }}
                                                        />
                                                    ))}
                                                </>
                                            )}

                                            {/* Image Container */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    width: hoveredCard === index ? '95%' : '90%',
                                                    height: hoveredCard === index ? '95%' : '90%',
                                                    borderRadius: '20px',
                                                    overflow: 'hidden',
                                                    background: 'rgba(255, 255, 255, 0.95)',
                                                    backdropFilter: 'blur(15px)',
                                                    border: hoveredCard === index 
                                                        ? `2px solid ${colors.accent}80`
                                                        : '2px solid rgba(255, 255, 255, 0.8)',
                                                    boxShadow: hoveredCard === index 
                                                        ? `0 20px 40px ${colors.primary}, inset 0 0 30px rgba(255, 255, 255, 0.5)`
                                                        : '0 10px 25px rgba(0, 0, 0, 0.1), inset 0 0 20px rgba(255, 255, 255, 0.3)',
                                                    transition: 'all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                    '&::before': hoveredCard === index ? {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: -4,
                                                        left: -4,
                                                        right: -4,
                                                        bottom: -4,
                                                        background: `linear-gradient(45deg, ${colors.accent}, transparent, ${colors.accent})`,
                                                        borderRadius: '24px',
                                                        opacity: 0.4,
                                                        filter: 'blur(8px)',
                                                        zIndex: -1,
                                                        animation: 'shimmer 2s ease-in-out infinite',
                                                        '@keyframes shimmer': {
                                                            '0%, 100%': { opacity: 0.3, filter: 'blur(8px)' },
                                                            '50%': { opacity: 0.6, filter: 'blur(12px)' },
                                                        }
                                                    } : {}
                                                }}
                                            >
                                                <img
                                                    src={item?.logo ? endpointsUrl + item.logo : "img/folder-2.png"}
                                                    alt={item?.title}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        padding: hoveredCard === index ? '8px' : '12px',
                                                        transition: 'all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                        filter: hoveredCard === index 
                                                            ? 'brightness(1.1) contrast(1.15) saturate(1.3) drop-shadow(0 0 20px rgba(0,0,0,0.1))' 
                                                            : 'brightness(1.05) contrast(1.05) saturate(1.1)',
                                                        transform: hoveredCard === index 
                                                            ? 'scale(1.1) rotateY(5deg) rotateX(2deg)' 
                                                            : 'scale(1) rotateY(0deg) rotateX(0deg)',
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Zoom>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                {/* Empty State */}
                {coursesList?.length === 0 && (
                    <Fade in={isVisible} timeout={1000}>
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 12,
                                color: 'rgba(100, 116, 139, 0.6)',
                            }}
                        >
                            <School sx={{ 
                                fontSize: 80, 
                                mb: 3, 
                                opacity: 0.3,
                                color: 'rgba(167, 139, 250, 0.4)'
                            }} />
                            <Box
                                sx={{
                                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.7) 100%)',
                                    backdropFilter: 'blur(20px)',
                                    border: '1px solid rgba(255, 255, 255, 0.8)',
                                    borderRadius: '24px',
                                    p: 4,
                                    maxWidth: 400,
                                    mx: 'auto',
                                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                                }}
                            >
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        mb: 2,
                                        color: 'rgba(51, 65, 85, 0.8)',
                                        fontWeight: 600
                                    }}
                                >
                                    No courses available
                                </Typography>
                                <Typography 
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(100, 116, 139, 0.7)',
                                        lineHeight: 1.6
                                    }}
                                >
                                    Courses will be displayed here once they are available.
                                </Typography>
                            </Box>
                        </Box>
                    </Fade>
                )}
            </Container>
        </Box>
    )
}

export default TestSeriesSection